import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Copy, MessageSquare, ShieldAlert, Target, Layers, Loader2, Drama } from "lucide-react";
import { safeCopy } from "@/lib/api";
import SalesPitchSim from "@/components/sales/SalesPitchSim";

const API = process.env.REACT_APP_BACKEND_URL;

const TOOLS = [
  { id: "script", icon: MessageSquare, title: "Outreach Script Generator", blurb: "Personalized cold script for any platform — your link auto-inserted" },
  { id: "objection", icon: ShieldAlert, title: "Objection Co-Pilot", blurb: "Paste what the prospect said — get the exact rebuttal to send back" },
  { id: "qualify", icon: Target, title: "Lead Qualifier", blurb: "Paste a chat transcript — score, signals and your next move" },
  { id: "dm_variants", icon: Layers, title: "DM Variant Builder", blurb: "Turn one script into 3 tonal variants ready to A/B" },
  { id: "simulator", icon: Drama, title: "Pitch Simulator", blurb: "Roleplay a tough buyer — get scored with coaching after" },
];

const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2 font-mono text-[11px] text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-orange focus:border-nc-orange";
const labelCls = "font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5";

function ToolForm({ tool, values, setValues }) {
  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));
  if (tool === "script") return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Platform</label>
          <select data-testid="ai-script-platform" className={inputCls} value={values.platform} onChange={(e) => set("platform", e.target.value)}>
            {["Email", "Discord DM", "LinkedIn", "Kickstarter"].map((p) => <option key={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Focus offer (optional)</label>
          <input data-testid="ai-script-focus" className={inputCls} placeholder="e.g. FiveM MLOs, UE5 optimization" value={values.offer_focus} onChange={(e) => set("offer_focus", e.target.value)} maxLength={300} />
        </div>
      </div>
      <div>
        <label className={labelCls}>Prospect details</label>
        <textarea data-testid="ai-script-prospect" rows={3} className={`${inputCls} resize-y`} placeholder={"Who are they? Server size, project, what they need…\ne.g. FiveM server owner, ~300 players, expanding custom map zones"} value={values.prospect} onChange={(e) => set("prospect", e.target.value)} maxLength={1500} />
      </div>
    </>
  );
  if (tool === "objection") return (
    <div>
      <label className={labelCls}>What the prospect said</label>
      <textarea data-testid="ai-objection-input" rows={3} className={`${inputCls} resize-y`} placeholder='e.g. "We already have internal technical artists, why would we need you?"' value={values.objection} onChange={(e) => set("objection", e.target.value)} maxLength={1500} />
    </div>
  );
  if (tool === "qualify") return (
    <div>
      <label className={labelCls}>Chat transcript</label>
      <textarea data-testid="ai-qualify-input" rows={6} className={`${inputCls} resize-y`} placeholder={"Paste the prospect conversation…\nProspect: we're building a UE5 survival game…\nYou: nice — what stage are you at?"} value={values.transcript} onChange={(e) => set("transcript", e.target.value)} maxLength={6000} />
    </div>
  );
  return (
    <div>
      <label className={labelCls}>Your script</label>
      <textarea data-testid="ai-variants-input" rows={5} className={`${inputCls} resize-y`} placeholder="Paste any outreach script to get 3 tonal variants…" value={values.script} onChange={(e) => set("script", e.target.value)} maxLength={3000} />
    </div>
  );
}

export default function SalesTools() {
  const [active, setActive] = useState("script");
  const [values, setValues] = useState({ platform: "Email", prospect: "", offer_focus: "", objection: "", transcript: "", script: "" });
  const [output, setOutput] = useState("");
  const [streaming, setStreaming] = useState(false);

  const canRun = () => {
    if (active === "script") return true;
    if (active === "objection") return values.objection.trim().length >= 5;
    if (active === "qualify") return values.transcript.trim().length >= 20;
    return values.script.trim().length >= 20;
  };

  const run = async () => {
    setStreaming(true);
    setOutput("");
    try {
      const token = localStorage.getItem("nc_token");
      const resp = await fetch(`${API}/api/sales/ai/${active}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(values),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `AI request failed (${resp.status})`);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        setOutput((prev) => prev + decoder.decode(value, { stream: true }));
      }
    } catch (err) {
      toast.error(err.message || "AI tool failed — try again");
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div data-testid="sales-ai-tools" className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-5 items-start">
      <div className="flex flex-col gap-2">
        <div className="border border-white/10 bg-black/40 px-4 py-3 font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" /> AI Sales Engine
        </div>
        {TOOLS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.id} data-testid={`ai-tool-${t.id}`} onClick={() => { setActive(t.id); setOutput(""); }} className={`text-left border px-4 py-3 transition-colors ${active === t.id ? "border-nc-orange/60 bg-nc-orange/10" : "border-white/10 hover:border-white/30"}`}>
              <p className="font-sans font-bold text-xs text-zinc-100 uppercase flex items-center gap-2"><Icon className="w-4 h-4 text-nc-orange shrink-0" /> {t.title}</p>
              <p className="font-mono text-[9px] text-zinc-500 mt-1 leading-relaxed">{t.blurb}</p>
            </button>
          );
        })}
        <p className="font-mono text-[8px] text-zinc-600 leading-relaxed px-1">Trained on your Master Guide — rebuttal matrix, proof points and tone rules included. 30 uses per day.</p>
      </div>

      <div className="border border-white/10 bg-black/40">
        <div className="border-b border-white/10 px-5 py-3.5">
          <p className="font-sans font-semibold uppercase tracking-widest text-sm text-zinc-100">{TOOLS.find((t) => t.id === active)?.title}</p>
        </div>
        <div className="p-5 flex flex-col gap-4">
          {active === "simulator" ? (
            <SalesPitchSim />
          ) : (
          <>
          <ToolForm tool={active} values={values} setValues={setValues} />
          <button data-testid="ai-run-button" disabled={streaming || !canRun()} onClick={run} className="self-start bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-black font-sans font-semibold uppercase tracking-[0.2em] text-[10px] px-5 py-2.5 flex items-center gap-2 transition-colors">
            {streaming ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {streaming ? "Generating…" : "Generate"}
          </button>
          {(output || streaming) && (
            <div className="border border-nc-orange/30 bg-[#050505]/80">
              <div className="flex items-center justify-between border-b border-white/10 px-4 py-2.5">
                <span className="font-mono text-[9px] tracking-[0.25em] text-nc-orange uppercase">{streaming ? "Streaming…" : "Result"}</span>
                <button data-testid="ai-copy-button" onClick={async () => { (await safeCopy(output)) ? toast.success("Copied") : toast.error("Copy blocked"); }} className="font-mono text-[9px] tracking-[0.2em] uppercase text-zinc-400 hover:text-white flex items-center gap-1.5 transition-colors">
                  <Copy className="w-3 h-3" /> Copy
                </button>
              </div>
              <pre data-testid="ai-output" className="font-mono text-[11px] text-zinc-200 leading-relaxed whitespace-pre-wrap p-4 max-h-[480px] overflow-y-auto">{output}{streaming && <span className="animate-pulse text-nc-orange">▊</span>}</pre>
            </div>
          )}
          </>
          )}
        </div>
      </div>
    </div>
  );
}
